import React, {useEffect, useState} from 'react';
import {Alert, Button, Dialog} from '@betareader/design-system';
import {withTranslation} from 'react-i18next';
import {authUtil} from '../../utils';
import Avatar from '../avatar/avatar';
import AppIcon from '../AppIcon';
import RatingStars from '../review/RatingStars';

export const UserReviewsModal = ({open, onClose, user, t}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    const fetchUserReviews = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const idToken = await authUtil.getFreshIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/users/${user._id}/reviews`,
          {method: 'GET', headers: {'Content-Type': 'application/json', 'br-token': idToken}}
        );
        if (!response.ok) throw new Error('Failed to fetch reviews');
        setReviews((await response.json()) || []);
      } catch (fetchError) {
        console.error('Failed to fetch user reviews:', fetchError);
        setError(fetchError.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserReviews();
  }, [open, user]);

  const handleClose = () => {
    setReviews([]);
    setError(null);
    onClose();
  };
  const formatDate = dateString => dateString
    ? new Date(dateString).toLocaleDateString() : t('UnknownDate');

  return <Dialog open={open} onClose={handleClose}
    title={<span style={{display: 'flex', alignItems: 'center', gap: 10}}>
      <Avatar name={user?.displayName} src={user?.photoUrl} diameter={30} />
      {t('ReviewsFor', {name: user?.displayName || 'User'})}
    </span>}
    actions={<Button onClick={handleClose}>{t('Close')}</Button>}>
    {isLoading ? (
      <div role='status' style={{textAlign: 'center', padding: '40px 0'}}>
        {t('LoadingReviews')}
      </div>
    ) : error ? (
      <Alert variant='error'><strong>{t('ErrorLoadingReviews')}</strong><p>{error}</p></Alert>
    ) : reviews.length === 0 ? (
      <Alert variant='info'><strong>{t('NoReviewsYet')}</strong>
        <p>{t('NoReviewsYetDescription')}</p></Alert>
    ) : (
      <div>
        <h3 style={{marginBottom: 20}}>
          {user?.averageRating ? <>
            <RatingStars label={t('OverallRating')} value={user.averageRating} />
            <span style={{fontWeight: 'normal', fontSize: '0.9em', marginLeft: 10}}>
              ({user.averageRating}/5)
            </span>
          </> : <AppIcon name='star' />}
          {' '}{t('ReviewCount', {count: reviews.length})}
        </h3>
        <ul style={{listStyle: 'none', padding: 0}}>
          {reviews.map((review, index) => <li key={review._id || index}
            style={{padding: '15px 0', borderBottom: '1px solid var(--br-border)'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8}}>
              {review.reviewer?.displayName && <Avatar
                name={review.reviewer.displayName} src={review.reviewer.photoUrl}
                diameter={25} />}
              <div>
                <strong>{review.reviewer?.displayName || t('Anonymous')}</strong>
                <div style={{fontSize: '0.8em', color: 'var(--br-muted)', marginTop: 2}}>
                  {formatDate(review.createdAt)}
                  {review.book?.title && <span> • {t('ForBook', {title: review.book.title})}</span>}
                </div>
              </div>
            </div>
            <RatingStars label={t('OverallRating')} value={review.rating} />
            {review.text && <blockquote style={{margin: '8px 0 0', padding: 10,
              background: 'var(--br-raised-surface)', borderRadius: 4,
              fontStyle: 'italic', fontSize: '0.9em', lineHeight: 1.4}}>
              {review.text}
            </blockquote>}
          </li>)}
        </ul>
      </div>
    )}
  </Dialog>;
};

export default withTranslation()(UserReviewsModal);
